import React from 'react';
import intl from 'react-intl-universal';
import {observer} from 'mobx-react-lite';

import type {AppState} from '@stores/AppState';
import type {AuditProgressTableRow} from '../../../components/AuditProgressTable/AuditProgressTable';
import {ContextMenu} from '../../../components/ContextMenu/ContextMenu';
import {getMuiIcon} from '../../../components/icons/createMuiIcon';
import {ContextMenuInterface} from '../ContextMenuInterface';

const AssignIcon = getMuiIcon('assignment_ind');
const ReassignIcon = getMuiIcon('sync_alt');
const CancelIcon = getMuiIcon('cancel');
const RunIcon = getMuiIcon('play_circle');
const RerunIcon = getMuiIcon('replay');

export interface AuditProgressRowOptionsHandlers {
    onAssign: (row: AuditProgressTableRow) => void;
    onReassign: (row: AuditProgressTableRow) => void;
    onCancelAssignment: (row: AuditProgressTableRow) => void;
    onRunNow: (row: AuditProgressTableRow) => void;
}

export interface AuditProgressRowOptionsProps extends ContextMenuInterface, AuditProgressRowOptionsHandlers {
    row: AuditProgressTableRow;
}

export const openAuditProgressRowOptions = (
    appState: AppState,
    row: AuditProgressTableRow,
    mousePositionParams: any,
    handlers: AuditProgressRowOptionsHandlers,
) => {
    appState.setContextMenu({
        ...mousePositionParams,
        id: `audit-progress-row-options-${row.auditTemplateId}:${row.locationId}`,
        type: 'audit-progress-row-options',
        withOverlay: true,
        minWidth: 200,
        params: {row, ...handlers},
    });
};

/** Item set depends on where this (template, location) pair stands:
 *  - never touched (no assignment, no run) — offer to assign it to someone or run it yourself now
 *  - actively assigned/in progress — offer to reassign or cancel that assignment
 *  - otherwise (completed, or a run exists outside any assignment) — offer to run it again */
export const AuditProgressRowOptions = observer(({handleClose, isDark, row, onAssign, onReassign, onCancelAssignment, onRunNow}: AuditProgressRowOptionsProps) => {
    const isActive = row.assignmentStatus === 'assigned' || row.assignmentStatus === 'in_progress';
    const isUntouched = !row.assignmentStatus && !row.lastRunId;

    const items = isActive
        ? [
            {
                text: intl.get('audits.progress.rowOptions.reassign').d('Reassign'),
                Icon: ReassignIcon,
                onClick: () => {
                    onReassign(row);
                    handleClose();
                    return false;
                },
            },
            {
                text: intl.get('audits.progress.rowOptions.cancelAssignment').d('Cancel assignment'),
                Icon: CancelIcon,
                onClick: () => {
                    onCancelAssignment(row);
                    handleClose();
                    return false;
                },
            },
        ]
        : isUntouched
            ? [
                {
                    text: intl.get('audits.progress.rowOptions.assign').d('Assign'),
                    Icon: AssignIcon,
                    onClick: () => {
                        onAssign(row);
                        handleClose();
                        return false;
                    },
                },
                {
                    text: intl.get('audits.progress.rowOptions.runNow').d('Run now'),
                    Icon: RunIcon,
                    onClick: () => {
                        onRunNow(row);
                        handleClose();
                        return false;
                    },
                },
            ]
            : [
                {
                    text: intl.get('audits.progress.rowOptions.rerun').d('Run again'),
                    Icon: RerunIcon,
                    onClick: () => {
                        onRunNow(row);
                        handleClose();
                        return false;
                    },
                },
            ];

    return (
        <ContextMenu
            isDark={isDark}
            handleClose={handleClose}
            items={items}
        />
    );
});
